import { AdminOperatorMessage } from "@domain/ai_operator"
import useTranslate from "@hooks/use_translate"
import { Box, Chip, Collapse, Link, Stack, Typography } from "@mui/material"
import dayjs from "dayjs"
import { useState } from "react"

import JsonBox from "./JsonBox"
import { blockSummary, formatMs } from "./operatorRun.utils"

export function Message({ message }: { message: AdminOperatorMessage }) {
  const { translate } = useTranslate()
  const [raw, setRaw] = useState(false)
  const isUser = message.role === "user"

  return (
    <Stack alignItems={isUser ? "flex-end" : "flex-start"}>
      <Box
        sx={{
          maxWidth: "85%",
          px: 1.5,
          py: 1,
          borderRadius: 2,
          bgcolor: isUser ? "primary.main" : "grey.100",
          color: isUser ? "primary.contrastText" : "text.primary"
        }}
      >
        {message.text ? (
          <Typography variant="body2" whiteSpace="pre-wrap">
            {message.text}
          </Typography>
        ) : null}
        {message.images?.map((image) => (
          <Link
            key={image.url}
            href={image.url}
            target="_blank"
            color="inherit"
            variant="caption"
          >
            {image.url}
          </Link>
        ))}
        {message.blocks.map((block, index) => (
          <Stack
            key={index}
            direction="row"
            gap={1}
            alignItems="baseline"
            mt={0.5}
          >
            <Chip
              size="small"
              label={block.k}
              sx={{ height: 18, fontSize: 11 }}
            />
            <Typography variant="body2" whiteSpace="pre-wrap">
              {blockSummary(block)}
            </Typography>
          </Stack>
        ))}
      </Box>
      <Stack direction="row" gap={1} mt={0.25} alignItems="center">
        <Typography variant="caption" color="text.secondary">
          {dayjs(message.createdAt).format("DD.MM HH:mm:ss")}
          {message.work ? ` · ${formatMs(message.work.durationMs)}` : ""}
        </Typography>
        {message.blocks.length ? (
          <Link
            component="button"
            variant="caption"
            onClick={() => setRaw(!raw)}
          >
            {translate("ai_operator_blocks_json")}
          </Link>
        ) : null}
      </Stack>
      <Collapse in={raw} sx={{ width: "100%" }}>
        <JsonBox
          label={translate("ai_operator_blocks_json")}
          value={message.blocks}
        />
      </Collapse>
    </Stack>
  )
}

export default function OperatorChatThread({
  messages
}: {
  messages: AdminOperatorMessage[]
}) {
  const { translate } = useTranslate()
  if (!messages.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        {translate("ai_operator_chat_empty")}
      </Typography>
    )
  }
  return (
    <Stack gap={1.5}>
      {messages.map((message) => (
        <Message key={message.id} message={message} />
      ))}
    </Stack>
  )
}
